import { LinearProgress, Modal, styled } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useEffect, useState } from 'react';
import theme from 'styles/theme';

interface IAdvancedLoaderProps {
  message?: string;
  tips?: string[];
}

const DEFAULT_TIPS = [
  'Validating file structure...',
  'Parsing geometries (GeoJSON/Shape/WKT)..',
  'Reprojecting coordinates (CRS)...',
  'De-duplicating features...',
  'Calculating feature statistics...',
  'Generating map previews...',
  'Snapping & simplifying topologies...',
  'Indexing for fast lookups...',
];

const describeWedge = (cx: number, cy: number, r: number, startDeg: number, endDeg: number) => {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const largeArc = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
};

const polar = (cx: number, cy: number, r: number, deg: number) => {
  const rad = (deg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const RadarContainer = styled('div')<{ size: number }>(({ size }) => ({
  width: size,
  height: size,
  position: 'relative',
}));

const RadarSvg = styled('svg')({
  display: 'block',
});

const ContentContainer = styled(FlexBox)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  background: theme.custom.themeColors.white,
  borderRadius: 4,
  height: '15rem',
  maxWidth: '42rem',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(4),
  gap: theme.spacing(4),
  outline: 'none',
}));

const pointPolarPositions = (r: number) => {
  const rings = [0.36, 0.54, 0.78];
  const pts: { x: number; y: number }[] = [];
  rings.forEach((ratio, i) => {
    const count = 7 + i * 2;
    for (let j = 0; j < count; j++) {
      const angle = (360 / count) * j + i * 12;
      pts.push(polar(r, r, (r - 6) * ratio, angle));
    }
  });
  return pts;
};

const Radar: FC<{ size?: number }> = ({ size = 128 }) => {
  const r = size / 2;
  const strokeW = 1;

  return (
    <RadarContainer size={size}>
      <RadarSvg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {/* Outer ring */}
        <circle
          cx={r}
          cy={r}
          r={r - strokeW}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeOpacity={0.25}
          strokeWidth={strokeW}
        />

        {/* Inner rings */}
        {[0.72, 0.52, 0.32].map((ratio, i) => (
          <circle
            key={i}
            cx={r}
            cy={r}
            r={(r - strokeW) * ratio}
            fill="none"
            stroke="gray"
            strokeOpacity={0.12 + i * 0.05}
            strokeWidth={1}
            strokeDasharray="3 6"
          />
        ))}

        {/* Sweep wedge */}
        <motion.g
          style={{ originX: r, originY: r }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, ease: 'linear', duration: 4.8 }}
        >
          <path
            d={describeWedge(r, r, r - strokeW - 2, -6, 36)}
            fill={theme.palette.primary.main}
            fillOpacity={0.22}
          />
          <path
            d={describeWedge(r, r, r - strokeW - 6, 36, 68)}
            fill={theme.palette.primary.main}
            fillOpacity={0.12}
          />
        </motion.g>

        {/* Pulsing points */}
        {pointPolarPositions(r).map((p, idx) => (
          <motion.circle
            key={idx}
            cx={p.x}
            cy={p.y}
            r={3}
            fill={theme.palette.primary.main}
            initial={{ opacity: 0.35, scale: 0.9 }}
            animate={{ opacity: [0.35, 1, 0.35], scale: [0.9, 1.25, 0.9] }}
            transition={{
              duration: 2 + (idx % 5) * 0.2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
      </RadarSvg>
    </RadarContainer>
  );
};

const AdvancedLoader: FC<IAdvancedLoaderProps> = ({
  message = 'Uploading and interpreting geo-data. This could take some time. Hang tight!',
  tips = DEFAULT_TIPS,
}) => {
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setTipIndex(i => (i + 1) % tips.length);
    }, 2800);
    return () => clearInterval(id);
  }, [tips.length]);

  return (
    <Modal
      open
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ContentContainer>
        <Radar size={128} />

        <FlexBox flexDirection="column" alignItems="normal" gap={2}>
          <ThemeTypography variant="TITLE_SMALL">{message}</ThemeTypography>

          <AnimatePresence mode="wait">
            <motion.div
              key={tipIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
            >
              <ThemeTypography variant="BODY_LARGE">{tips[tipIndex]}</ThemeTypography>
            </motion.div>
          </AnimatePresence>

          <LinearProgress />
        </FlexBox>
      </ContentContainer>
    </Modal>
  );
};

export default AdvancedLoader;
